import { ReactNode, useEffect, useState } from 'react';

// Small form controls shared by the settings pages (the same shapes the app's settings screen uses).

export const SwitchControl = ({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label: string;
}) => (
  <button
    type='button'
    role='switch'
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? 'bg-primary' : 'bg-border'
    }`}
  >
    <span
      aria-hidden='true'
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// label on the left, switch on the right
export const SwitchRow = ({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) => (
  <div className='flex items-center justify-between gap-3 text-sm'>
    <span>{label}</span>
    <SwitchControl checked={checked} onChange={onChange} disabled={disabled} label={label} />
  </div>
);

// A row of boxes of which one is selected (Light / Dark, Off / On).
export function SegmentedBoxes<T extends string | boolean>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: { label: string; value: T }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className='flex flex-col gap-2 text-sm'>
      <span>{label}</span>
      <div className='flex gap-2' role='radiogroup' aria-label={label}>
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={String(option.value)}
              type='button'
              role='radio'
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={`min-w-20 rounded-md border px-4 py-2 transition disabled:cursor-not-allowed disabled:opacity-60 ${
                selected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// "Off" is 0: the switch turns the setting off (0) or back on (to `onValue`), and the number is only
// editable while on - the same behaviour as the app's settings rows. The typed text is kept locally, so
// clearing the box to type another number does not count as Off: only valid numbers above 0 are reported.
export const NumberToggleRow = ({
  label,
  value,
  onValue,
  min = 0,
  max,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onValue: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) => {
  const isOn = value > 0;
  const [text, setText] = useState(String(value));

  // follow outside changes (switch, reload) without clobbering what is being typed ("2." stays "2.")
  useEffect(() => {
    setText((prev) => (Number(prev) === value ? prev : String(value)));
  }, [value]);

  const handleText = (next: string) => {
    setText(next);
    const parsed = Number(next);
    if (next.trim() === '' || !Number.isFinite(parsed) || parsed <= 0) return;
    onChange(max !== undefined ? Math.min(parsed, max) : parsed);
  };

  // leaving the box empty or at 0 restores the last valid number
  const handleBlur = () => setText(String(value));

  return (
    <div className='flex flex-col gap-2 text-sm'>
      <span>{label}</span>
      <div className='flex items-center gap-3'>
        <SwitchControl checked={isOn} onChange={(on) => onChange(on ? onValue : 0)} disabled={disabled} label={label} />
        <input
          type='number'
          min={min}
          max={max}
          value={text}
          disabled={disabled || !isOn}
          aria-label={label}
          onChange={(e) => handleText(e.target.value)}
          onBlur={handleBlur}
          className='w-full max-w-40 rounded-md border border-border bg-background px-3 py-2 text-foreground disabled:opacity-60'
        />
      </div>
    </div>
  );
};

export const SettingsCard = ({ children }: { children: ReactNode }) => (
  <section className='flex flex-col gap-5 rounded-lg border border-border bg-card p-4 sm:p-5'>{children}</section>
);
